import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material/styles';
import {
  DELETE_REGULARISATION_BY_ADMIN,
  GET_PRIME_REGULARISATION_BY_TAM_AND_MONTH,
  UPSERT_REGULARISATION_BY_ADMIN,
} from '@bloomays-lib/adapter.api-talent';
import { ButtonV2, DrawerV2, InputV2, TextareaV2, dsV2 } from '@bloomays-lib/ui.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { GetPrimeRegularisationByTamAndMonthQueryData, PrimeRegularisationQueryRow } from '../../types/primes';
import { formatMonthYearToMonthLabel, formatPrimeEuroWithSign } from '../../utils/monthlyPrime.utils';

export type RegularisationDirection = 'add' | 'subtract';

export type TamRegularisationDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  tamEmail: string;
  tamDisplayName: string;
  referenceDate: Date;
};

const SAVE_GRADIENT = 'linear-gradient(135deg, #ff3328 0%, #ff6b97 100%)';
const SAVE_GRADIENT_HOVER = 'linear-gradient(135deg, #e62e24 0%, #f05a88 100%)';

const hasAtMostTwoDecimalPlaces = (total: number): boolean => total === Math.round(total * 100) / 100;

const parseAmountInput = (raw: string): number => {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '') {
    return Number.NaN;
  }
  return Number(normalized);
};

const isAmountValid = (raw: string): boolean => {
  const parsed = parseAmountInput(raw);
  return Number.isFinite(parsed) && parsed > 0 && hasAtMostTwoDecimalPlaces(parsed);
};

const isLegacyReason = (reason: string): boolean => {
  const trimmed = reason.trim();
  return trimmed === '' || trimmed === '/';
};

const toFormReason = (reason: string): string => (reason.trim() === '/' ? '' : reason);

const TamRegularisationDrawer = ({
  open,
  onClose,
  onSaved,
  tamEmail,
  tamDisplayName: _tamDisplayName,
  referenceDate,
}: TamRegularisationDrawerProps) => {
  const { t } = useTranslation(['primes']);
  const monthYear = useMemo(() => convertDateToStringFormat(referenceDate, 'MM/yyyy'), [referenceDate]);
  const monthLabel = useMemo(() => {
    const year = monthYear.split('/')[1] ?? '';
    return `${formatMonthYearToMonthLabel(monthYear)} ${year}`;
  }, [monthYear]);

  const [direction, setDirection] = useState<RegularisationDirection>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [invalidAmountMessage, setInvalidAmountMessage] = useState<string | undefined>();
  const [loadedRegularisation, setLoadedRegularisation] = useState<PrimeRegularisationQueryRow | null>(null);
  const [isLoadingRegularisation, setIsLoadingRegularisation] = useState(false);
  const [loadError, setLoadError] = useState<Error | undefined>();

  const [loadRegularisation] = useLazyQuery<GetPrimeRegularisationByTamAndMonthQueryData>(
    GET_PRIME_REGULARISATION_BY_TAM_AND_MONTH,
    {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'network-only',
    },
  );

  const [upsertRegularisation, { loading: isSaving }] = useMutation(UPSERT_REGULARISATION_BY_ADMIN, {
    context: { clientName: 'api.talents' },
    onError: (mutationError) => {
      errorLogger(mutationError, { extraInfos: 'upsertRegularisationByAdmin failed in TamRegularisationDrawer' });
      notify('error', t('regularisationSaveError'), mutationError);
    },
  });

  const [deleteRegularisation, { loading: isDeleting }] = useMutation(DELETE_REGULARISATION_BY_ADMIN, {
    context: { clientName: 'api.talents' },
    onCompleted: () => {
      notify('success', t('regularisationDeleteSuccess'));
      onSaved?.();
      onClose();
    },
    onError: (mutationError) => {
      errorLogger(mutationError, { extraInfos: 'deleteRegularisationByAdmin failed in TamRegularisationDrawer' });
      notify('error', t('regularisationDeleteError'), mutationError);
    },
  });

  const isEditMode = loadedRegularisation !== null;
  const isRegularisationLocked = loadedRegularisation?.status === 'validated';
  const hasLegacyReason = loadedRegularisation !== null && isLegacyReason(loadedRegularisation.reason);
  const isBusy = isLoadingRegularisation || isSaving || isDeleting;
  const fieldsDisabled = isBusy || loadError !== undefined || isRegularisationLocked;
  const isSaveBlocked =
    fieldsDisabled || reason.trim().length === 0 || !isAmountValid(amount) || isRegularisationLocked;

  const applyLoadedRegularisation = useCallback((regularisation: PrimeRegularisationQueryRow) => {
    setDirection(regularisation.total < 0 ? 'subtract' : 'add');
    setAmount(String(Math.abs(regularisation.total)));
    setReason(toFormReason(regularisation.reason));
    setReasonTouched(false);
    setInvalidAmountMessage(undefined);
  }, []);

  const resetCreateForm = useCallback(() => {
    setDirection('add');
    setAmount('');
    setReason('');
    setReasonTouched(false);
    setInvalidAmountMessage(undefined);
  }, []);

  useEffect(() => {
    if (!open) {
      setLoadedRegularisation(null);
      setLoadError(undefined);
      setIsLoadingRegularisation(false);
      return;
    }

    let cancelled = false;
    setLoadedRegularisation(null);
    setLoadError(undefined);
    resetCreateForm();
    setIsLoadingRegularisation(true);

    void loadRegularisation({
      variables: { tamEmail, monthYear },
    })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const regularisation = result.data?.getPrimeRegularisationByTamAndMonth ?? null;
        setLoadedRegularisation(regularisation);
        if (regularisation === null) {
          resetCreateForm();
          return;
        }
        applyLoadedRegularisation(regularisation);
      })
      .catch((error: Error) => {
        if (cancelled) {
          return;
        }
        setLoadError(error);
        errorLogger(error, { extraInfos: 'getPrimeRegularisationByTamAndMonth failed in TamRegularisationDrawer' });
        notify('error', t('regularisationLoadError'), error);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingRegularisation(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // `t` is intentionally omitted: an unstable reference would re-fetch and reset the form on each keystroke.
  }, [applyLoadedRegularisation, loadRegularisation, monthYear, open, resetCreateForm, tamEmail]);

  const handleClose = useCallback(() => {
    if (!isBusy) {
      onClose();
    }
  }, [isBusy, onClose]);

  const handleSave = useCallback(() => {
    if (loadError || isRegularisationLocked) {
      return;
    }
    setReasonTouched(true);
    const parsedAmount = parseAmountInput(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0 || !hasAtMostTwoDecimalPlaces(parsedAmount)) {
      setInvalidAmountMessage(t('regularisationInvalidAmount'));
      return;
    }
    const trimmedReason = reason.trim();
    if (trimmedReason.length === 0) {
      return;
    }
    setInvalidAmountMessage(undefined);
    const signedTotal = direction === 'add' ? parsedAmount : -parsedAmount;
    const savingAsEdit = loadedRegularisation !== null;
    void upsertRegularisation({
      variables: {
        input: {
          tamEmail,
          monthYear,
          total: signedTotal,
          reason: trimmedReason,
        },
      },
    }).then(() => {
      notify('success', savingAsEdit ? t('regularisationEditSuccess') : t('regularisationAddSuccess'));
      onSaved?.();
      onClose();
    });
  }, [
    amount,
    direction,
    isRegularisationLocked,
    loadError,
    loadedRegularisation,
    monthYear,
    onClose,
    onSaved,
    reason,
    t,
    tamEmail,
    upsertRegularisation,
  ]);

  const handleDelete = useCallback(() => {
    if (isRegularisationLocked) {
      return;
    }
    void deleteRegularisation({
      variables: { tamEmail, monthYear },
    });
  }, [deleteRegularisation, isRegularisationLocked, monthYear, tamEmail]);

  const drawerTitle = isEditMode ? t('regularisationDrawerTitleEdit') : t('regularisationDrawerTitleAdd');

  return (
    <DrawerV2
      open={open}
      onClose={handleClose}
      title={drawerTitle}
      subtitle={t('regularisationDrawerSubtitle', { monthLabel })}
      footer={
        <FooterBar>
          {isEditMode && !isRegularisationLocked ? (
            <ButtonV2
              variant="secondary"
              disabled={isBusy}
              startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={handleDelete}
              ariaLabel="regularisation-delete"
              sx={{
                color: dsV2.color.rouge,
                borderColor: dsV2.color.rougeBrd,
                '&:hover': { backgroundColor: dsV2.color.rougeDim, borderColor: dsV2.color.rougeBrd },
              }}
            >
              {t('regularisationDelete')}
            </ButtonV2>
          ) : (
            <span />
          )}
          <FooterActions>
            <ButtonV2 variant="secondary" disabled={isBusy} onClick={handleClose} ariaLabel="regularisation-cancel">
              {t('regularisationCancel')}
            </ButtonV2>
            {!isRegularisationLocked ? (
              <ButtonV2
                disabled={isSaveBlocked}
                loading={isSaving}
                onClick={handleSave}
                ariaLabel="regularisation-save"
                sx={{
                  background: SAVE_GRADIENT,
                  color: dsV2.color.blanc,
                  border: 'none',
                  boxShadow: '0 2px 10px rgba(255, 51, 40, 0.28)',
                  '&:hover': { background: SAVE_GRADIENT_HOVER, boxShadow: 'none' },
                  '&.Mui-disabled': {
                    background: dsV2.color.g200,
                    color: dsV2.color.g400,
                    boxShadow: 'none',
                  },
                }}
              >
                {t(isEditMode ? 'regularisationSaveEdit' : 'regularisationSaveAdd')}
              </ButtonV2>
            ) : null}
          </FooterActions>
        </FooterBar>
      }
    >
      <FormStack>
        {isRegularisationLocked ? (
          <LockedBanner>
            <InfoOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <span>{t('regularisationLockedValidated')}</span>
          </LockedBanner>
        ) : null}

        <FormField>
          <SectionLabel>{t('regularisationAmountLabel')}</SectionLabel>
          <AmountRow>
            <DirectionToggle role="group" aria-label={t('regularisationAmountLabel')}>
              <DirectionOption
                type="button"
                $active={direction === 'add'}
                $tone="add"
                disabled={fieldsDisabled}
                data-testid="direction-add"
                onClick={() => setDirection('add')}
              >
                {t('regularisationDirectionAdd')}
              </DirectionOption>
              <DirectionOption
                type="button"
                $active={direction === 'subtract'}
                $tone="subtract"
                disabled={fieldsDisabled}
                data-testid="direction-subtract"
                onClick={() => setDirection('subtract')}
              >
                {t('regularisationDirectionSubtract')}
              </DirectionOption>
            </DirectionToggle>

            <AmountInputWrap data-testid="regularisation-amount-input">
              <InputV2
                value={amount}
                type="number"
                onChange={handleAmountChange}
                disabled={fieldsDisabled}
                readOnly={isRegularisationLocked}
                ariaLabel="regularisation-amount"
                min={0}
              />
              <AmountSuffix>€</AmountSuffix>
            </AmountInputWrap>
          </AmountRow>
          {invalidAmountMessage ? <FieldError>{invalidAmountMessage}</FieldError> : null}
        </FormField>

        <FormField>
          <SectionLabel>
            {t('regularisationReasonLabel')}
            <RequiredMark>*</RequiredMark>
          </SectionLabel>
          <TextareaWrap data-testid="regularisation-reason-input">
            <TextareaV2
              value={reason}
              onChange={(value) => {
                setReason(value);
                setReasonTouched(true);
              }}
              rows={4}
              disabled={fieldsDisabled}
              placeholder={t('regularisationReasonPlaceholder')}
              ariaLabel="regularisation-reason"
            />
          </TextareaWrap>
          {hasLegacyReason && !reasonTouched && reason.trim().length === 0 ? (
            <FieldHint>{t('regularisationLegacyReasonHint')}</FieldHint>
          ) : null}
          {reasonTouched && reason.trim().length === 0 ? (
            <FieldError>{t('regularisationReasonRequired')}</FieldError>
          ) : null}
        </FormField>

        {isEditMode && loadedRegularisation !== null && !isRegularisationLocked ? (
          <ExistingSection>
            <ExistingSectionTitle>{t('regularisationExistingSectionTitle')}</ExistingSectionTitle>
            <ExistingEntryCard
              type="button"
              disabled={fieldsDisabled}
              data-testid="regularisation-existing-entry"
              onClick={() => applyLoadedRegularisation(loadedRegularisation)}
            >
              <ExistingEntryMain>
                <ExistingEntryReason>
                  {isLegacyReason(loadedRegularisation.reason) ? '—' : loadedRegularisation.reason}
                </ExistingEntryReason>
              </ExistingEntryMain>
              <ExistingEntryAmount $negative={loadedRegularisation.total < 0}>
                {formatPrimeEuroWithSign(loadedRegularisation.total)}
              </ExistingEntryAmount>
            </ExistingEntryCard>
          </ExistingSection>
        ) : null}
      </FormStack>
    </DrawerV2>
  );

  function handleAmountChange(value: string) {
    setAmount(value);
    if (value.trim() === '') {
      setInvalidAmountMessage(undefined);
      return;
    }
    if (!isAmountValid(value)) {
      setInvalidAmountMessage(t('regularisationInvalidAmount'));
      return;
    }
    setInvalidAmountMessage(undefined);
  }
};

export default TamRegularisationDrawer;

const FormStack = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

const FormField = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const SectionLabel = styled('div')({
  fontFamily: dsV2.font.sansSemiBold,
  fontSize: 13,
  fontWeight: 600,
  color: dsV2.color.noir,
});

const RequiredMark = styled('span')({
  color: dsV2.color.rouge,
  marginLeft: 2,
});

const FieldError = styled('div')({
  fontFamily: dsV2.font.sans,
  fontSize: 10.5,
  color: dsV2.color.rouge,
});

const FieldHint = styled('div')({
  fontFamily: dsV2.font.sans,
  fontSize: 10.5,
  color: dsV2.color.amberDark,
});

const LockedBanner = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '12px 14px',
  borderRadius: dsV2.radius.lg,
  border: `1px solid ${dsV2.color.amberBrd}`,
  backgroundColor: dsV2.color.amberDim,
  color: dsV2.color.amberDark,
  fontFamily: dsV2.font.sans,
  fontSize: 12.5,
  lineHeight: 1.45,
});

const TextareaWrap = styled('div')({
  width: '100%',
});

const AmountRow = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  gap: 10,
});

const DirectionToggle = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  padding: 3,
  borderRadius: dsV2.radius.round,
  backgroundColor: dsV2.color.surface2,
  border: `1px solid ${dsV2.color.border}`,
  flexShrink: 0,
});

const DirectionOption = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$tone',
})<{ $active: boolean; $tone: RegularisationDirection }>(({ $active, $tone }) => {
  const accent = $tone === 'subtract' ? dsV2.color.rouge : dsV2.color.vert;
  const accentBrd = $tone === 'subtract' ? dsV2.color.rougeBrd : dsV2.color.vertBrd;

  return {
    border: '1px solid transparent',
    borderRadius: dsV2.radius.round,
    padding: '7px 14px',
    fontFamily: dsV2.font.sansSemiBold,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color .15s ease, border-color .15s ease, color .15s ease',
    backgroundColor: $active ? dsV2.color.blanc : 'transparent',
    color: $active ? accent : dsV2.color.t3,
    borderColor: $active ? accentBrd : 'transparent',
    boxShadow: $active ? '0 1px 4px rgba(33,33,33,0.08)' : 'none',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  };
});

const AmountInputWrap = styled('div')({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  '& input': {
    paddingRight: 34,
    textAlign: 'right',
    fontFamily: dsV2.font.sansSemiBold,
    fontWeight: 600,
  },
});

const AmountSuffix = styled('span')({
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontFamily: dsV2.font.sansSemiBold,
  fontSize: 13,
  fontWeight: 600,
  color: dsV2.color.t3,
  pointerEvents: 'none',
});

const ExistingSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 4,
});

const ExistingSectionTitle = styled('div')({
  fontFamily: dsV2.font.sansSemiBold,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: dsV2.color.t4,
});

const ExistingEntryCard = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
  padding: '12px 14px',
  borderRadius: dsV2.radius.lg,
  border: `1px solid ${dsV2.color.rougeBrd}`,
  backgroundColor: dsV2.color.rougeLight,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background-color .15s ease, border-color .15s ease',
  '&:hover:not(:disabled)': {
    backgroundColor: dsV2.color.rougeDim,
    borderColor: dsV2.color.rougeBrd,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.7,
  },
});

const ExistingEntryMain = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  minWidth: 0,
  flex: 1,
});

const ExistingEntryReason = styled('span')({
  fontFamily: dsV2.font.sans,
  fontSize: 13,
  color: dsV2.color.t2,
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

const ExistingEntryAmount = styled('span', {
  shouldForwardProp: (prop) => prop !== '$negative',
})<{ $negative: boolean }>(({ $negative }) => ({
  fontFamily: dsV2.font.sansSemiBold,
  fontSize: 14,
  fontWeight: 600,
  color: $negative ? dsV2.color.rouge : dsV2.color.vert,
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

const FooterBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
});

const FooterActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});
